import * as Sentry from '@sentry/react';
import { QueryClient, QueryFunction } from '@tanstack/react-query';

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    const error = new Error(`${res.status}: ${text}`);

    // Log to Sentry for server errors (5xx)
    if (res.status >= 500) {
      Sentry.captureException(error, {
        tags: {
          type: 'query_error',
          status_code: res.status,
        },
        extra: {
          url: res.url,
          status: res.status,
          statusText: res.statusText,
          responseText: text,
        },
        level: 'error',
      });
    } else if (res.status >= 400 && res.status !== 401) {
      // Add breadcrumb for client errors (4xx) except 401 (unauthorized is common)
      Sentry.addBreadcrumb({
        category: 'query',
        message: `Request to ${res.url} failed with ${res.status}`,
        level: 'warning',
        data: {
          url: res.url,
          status: res.status,
        },
      });
    }

    throw error;
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined
): Promise<Response> {
  const res = await fetch(url, {
    method,
    headers: data ? { 'Content-Type': 'application/json' } : {},
    body: data ? JSON.stringify(data) : undefined,
    credentials: 'include',
  });

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = 'returnNull' | 'throw';
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const res = await fetch(queryKey[0] as string, {
      credentials: 'include',
    });

    if (unauthorizedBehavior === 'returnNull' && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: 'throw' }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
      onError: (error) => {
        // Log mutation errors to Sentry if they look like server errors
        const errorMessage =
          error instanceof Error ? error.message : String(error);
        const statusMatch = errorMessage.match(/^(\d{3}):/);
        const status = statusMatch ? parseInt(statusMatch[1], 10) : null;

        if (status && status >= 500) {
          Sentry.captureException(error, {
            tags: {
              type: 'mutation_error',
              status_code: status,
            },
            level: 'error',
          });
        }
      },
    },
  },
});
